import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, Outlet } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useTrialStatus } from '../hooks/useTrialStatus';
import { useFeatureAccess } from '../hooks/useFeatureAccess';
import { usePageVisit, useVisitListener } from '../hooks/usePageVisit';
import { NewBadge } from './NewBadge';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import Logo from './Logo';
import {
  Truck,
  ClipboardList,
  ShoppingCart,
  Users,
  Package,
  LogOut,
  Menu,
  X,
  PackageCheck,
  Boxes,
  BarChart3,
  Settings2,
  UserCircle,
  Loader2,
  ChevronDown,
  ChevronRight,
  Crown,
  Building2,
  Star,
  Clock
} from 'lucide-react';
import { toast } from 'sonner';

const Layout = () => {
  const { user, logout, isAdmin, isSuperAdmin, api, login } = useAuth();
  const { getCartCount } = useCart();
  const { trialDaysLeft, isTrialExpired, showTrialBanner } = useTrialStatus();
  const { canUseFavorites, canUseLogo } = useFeatureAccess();
  const { hasVisitedPage } = usePageVisit();
  
  // Écouter les changements de visite pour re-render les badges
  useVisitListener();
  
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  
  // Quick switch user dialog
  const [switchDialogOpen, setSwitchDialogOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [switchingUser, setSwitchingUser] = useState(null);
  const [passwordInput, setPasswordInput] = useState('');
  const [switchLoading, setSwitchLoading] = useState(false);
  
  // Sidebar sections expand state
  const [expandedSections, setExpandedSections] = useState({
    stocks: true,
    admin: true
  });

  const [restaurantLogo, setRestaurantLogo] = useState(null);

  useEffect(() => {
    const saved = localStorage.getItem('sidebarExpanded');
    if (saved !== null) setSidebarExpanded(JSON.parse(saved));
  }, []);

  // Load restaurant logo (only if plan allows it)
  useEffect(() => {
    if (isAdmin && user?.restaurant_id && canUseLogo) {
      api.get('/restaurant/settings').then(r => {
        if (r.data.logo_base64) setRestaurantLogo(r.data.logo_base64);
      }).catch(() => {});
    } else {
      setRestaurantLogo(null); // Reset logo if plan doesn't allow it
    }
  }, [isAdmin, user?.restaurant_id, canUseLogo, api]);

  const toggleSidebar = () => {
    const newState = !sidebarExpanded;
    setSidebarExpanded(newState);
    localStorage.setItem('sidebarExpanded', JSON.stringify(newState));
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleLogoClick = () => {
    if (window.innerWidth >= 1024) {
      toggleSidebar();
    } else {
      if (isAdmin) {
        navigate('/dashboard');
      } else {
        navigate('/catalog');
      }
      setMobileMenuOpen(false);
    }
  };

  // Quick switch user functions
  const openSwitchDialog = async () => {
    setSwitchDialogOpen(true);
    setLoadingUsers(true);
    try {
      const response = await api.get('/users/switch-list');
      setUsers(response.data.filter(u => u.id !== user.id));
    } catch (error) {
      toast.error('Erreur lors du chargement des utilisateurs');
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleQuickSwitch = async (targetUser) => {
    if (!passwordInput) {
      toast.error('Veuillez entrer le mot de passe');
      return;
    }
    
    setSwitchLoading(true);
    try {
      await login(targetUser.email, passwordInput);
      setSwitchDialogOpen(false);
      setPasswordInput('');
      setSwitchingUser(null);
      toast.success(`Connecté en tant que ${targetUser.first_name}`);
      if (targetUser.is_admin) {
        navigate('/dashboard');
      } else {
        navigate('/catalog');
      }
    } catch (error) {
      toast.error('Mot de passe incorrect');
    } finally {
      setSwitchLoading(false);
    }
  };

  const navItems = [
    { to: '/catalog', label: 'Commander', icon: ShoppingCart },
    { to: '/orders', label: 'Commandes', icon: ClipboardList },
    { to: '/deliveries', label: 'Livraisons', icon: PackageCheck },
    ...(canUseFavorites ? [{ to: '/admin/modeles', label: 'Favoris', icon: Star }] : []),
  ];

  const adminItems = [
    { to: '/dashboard', label: 'Tableau de bord', icon: BarChart3 },
    { to: '/admin/users', label: 'Utilisateurs', icon: Users },
    { to: '/admin/teams', label: 'Équipes', icon: Settings2 },
    { to: '/admin/suppliers', label: 'Fournisseurs', icon: Truck },
    { to: '/admin/products', label: 'Produits', icon: Boxes },
    { to: '/admin/settings', label: 'Paramètres', icon: Building2 },
  ];

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const cartCount = getCartCount();
  const sidebarCollapsed = !sidebarExpanded;

  const NavItem = ({ to, label, icon: Icon, badge, collapsed }) => {
    const isNewPage = !hasVisitedPage(to) && to !== '/cart'; // Ne pas afficher le badge "nouveau" sur le Panier
    
    return (
      <NavLink
        to={to}
        onClick={() => setMobileMenuOpen(false)}
        className={({ isActive }) =>
          `group flex items-center gap-3 px-3 py-3 rounded-xl transition-all duration-200 relative ${
            isActive
              ? 'bg-[#1B4332] text-white shadow-lg shadow-[#1B4332]/20'
              : 'text-gray-600 hover:bg-[#D8F3DC] hover:text-[#1B4332]'
          } ${collapsed ? 'justify-center' : ''}`
        }
        data-testid={`nav-${label.toLowerCase().replace(/\s/g, '-')}`}
        title={collapsed ? label : undefined}
      >
        <Icon className={`w-5 h-5 flex-shrink-0 transition-transform duration-200 ${collapsed ? '' : 'group-hover:scale-110'}`} />
        {!collapsed && (
          <>
            <span className="font-medium whitespace-nowrap">{label}</span>
            <div className="flex items-center gap-2 ml-auto">
              {badge && badge > 0 && (
                <Badge className="bg-[#D8F3DC] text-[#1B4332] text-xs px-2">{badge}</Badge>
              )}
              {isNewPage && <NewBadge />}
            </div>
          </>
        )}
        {collapsed && badge && badge > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#1B4332] text-white text-xs rounded-full flex items-center justify-center">
            {badge}
          </span>
        )}
        {collapsed && isNewPage && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#1B4332] text-white text-xs rounded-full flex items-center justify-center font-semibold">
            1
          </span>
        )}
      </NavLink>
    );
  };

  const sidebarWidth = sidebarCollapsed ? 'w-20' : 'w-64';
  const mainMargin = sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64';

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200 shadow-sm safe-area-top">
        <div className="h-14 px-4 flex items-center justify-between">
          <button onClick={handleLogoClick} className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <Logo size="default" />
          </button>
          <div className="flex items-center gap-2">
            {cartCount > 0 && (
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate('/cart')}
                className="relative"
              >
                <ShoppingCart className="w-5 h-5 text-[#1B4332]" />
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#1B4332] text-white text-xs rounded-full flex items-center justify-center">
                  {cartCount}
                </span>
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </Button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 z-40 bg-black/50 backdrop-blur-sm"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`fixed left-0 z-50 h-screen bg-white border-r border-gray-100 shadow-xl transition-all duration-300 ease-in-out
          ${sidebarWidth}
          ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'} 
          lg:translate-x-0`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className={`${showTrialBanner ? 'p-3' : 'p-4'} border-b border-gray-100 ${sidebarCollapsed ? 'px-3' : 'px-5'} flex-shrink-0`}>
            <div className={`flex items-center ${sidebarCollapsed ? 'justify-center' : 'gap-3'}`}>
              <button 
                onClick={handleLogoClick}
                className={`flex items-center ${sidebarCollapsed ? '' : 'gap-3'} hover:opacity-80 transition-opacity cursor-pointer`}
                title={sidebarCollapsed ? "Déplier le menu" : "Replier le menu"}
              >
                {/* Logo Icon - 75% de la taille précédente (42px) */}
                <div className="w-[42px] h-[42px] flex items-center justify-center flex-shrink-0">
                  {restaurantLogo ? (
                    <img src={restaurantLogo} alt="Logo" className="w-full h-full object-contain" />
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1620.5 2059" className="w-full h-full">
                      <g transform="translate(63.27 60)">
                        <path d="M305.846-60h887.538a68,68,0,0,1,68,68V588.658l292.357,877.069a68,68,0,0,1,3.49,21.5c0,368.283-226.434,511.769-807.615,511.769-399.748,0-617.447-53.392-727.934-178.531-71.814-81.338-97.707-193.1-79.158-341.663a68.017,68.017,0,0,1,2.966-13.079L237.846,588.658V8A68,68,0,0,1,305.846-60ZM1125.385,76H373.846V599.693a68,68,0,0,1-3.49,21.5l-293.668,881c-12.23,105.544,2.741,178.192,46.943,228.256C204.564,1822.122,397.625,1863,749.615,1863c610.086,0,669.648-164.208,671.565-364.886L1128.874,621.2a68,68,0,0,1-3.49-21.5Z" fill="#1b4332"/>
                        <path d="M1183,68H0A68,68,0,0,1-68,0,68,68,0,0,1,0-68H1183a68,68,0,0,1,68,68A68,68,0,0,1,1183,68Z" transform="translate(158 8)" fill="#1b4332"/>
                        <path d="M236.889,675.692q-1.28,0-2.565-.048a68,68,0,0,1-51.839-27.152L-39.4,352.646a68,68,0,1,1,108.8-81.6L240.951,499.781,703.44-28.778a68,68,0,0,1,102.35,89.557L288.06,652.471A68,68,0,0,1,236.889,675.692Z" transform="translate(364.692 902.692)" fill="#1b4332"/>
                      </g>
                    </svg>
                  )}
                </div>
                
                {/* Text Block - Hauteur égale au logo (42px) */}
                {!sidebarCollapsed && (
                  <div className="flex flex-col justify-center h-[42px]">
                    {/* ORDER LAB - Réduit */}
                    <h1 className="font-black text-[#1B4332] text-base tracking-tight uppercase leading-none">
                      ORDER LAB
                    </h1>
                    {/* GESTION DE STOCK - Réduit */}
                    <p className="text-[9px] text-gray-500 tracking-[0.12em] uppercase mt-0.5 leading-none">
                      GESTION DE STOCK
                    </p>
                    {/* Nom du restaurant - Réduit */}
                    <p className="text-[11px] font-semibold text-[#00D66C] mt-1 leading-none">
                      {user?.restaurant_name || 'Restaurant'}
                    </p>
                  </div>
                )}
              </button>
            </div>
          </div>

          {/* Navigation */}
          <nav className={`flex-1 overflow-y-auto sidebar-scroll ${sidebarCollapsed ? 'px-2 py-4' : 'p-4'} pb-2`}>
            {/* STOCKS Section */}
            <div className="mb-4">
              {!sidebarCollapsed && (
                <button 
                  onClick={() => toggleSection('stocks')}
                  className="w-full flex items-center justify-between px-3 py-2 mb-2 text-[10px] font-semibold text-gray-400 uppercase tracking-widest hover:text-gray-600 transition-colors"
                >
                  <span className="flex items-center gap-2">
                    <Package className="w-3.5 h-3.5" />
                    Stocks
                  </span>
                  {expandedSections.stocks ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                </button>
              )}
              {(sidebarCollapsed || expandedSections.stocks) && (
                <div className="space-y-1.5">
                  {navItems.map((item) => (
                    <NavItem key={item.to} {...item} collapsed={sidebarCollapsed} />
                  ))}
                  {cartCount > 0 && (
                    <div className="relative">
                      <NavItem to="/cart" label="Panier" icon={ShoppingCart} badge={cartCount} collapsed={sidebarCollapsed} />
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Admin Section */}
            {isAdmin && !isSuperAdmin && (
              <div className="mb-4">
                {!sidebarCollapsed && (
                  <button 
                    onClick={() => toggleSection('admin')}
                    className="w-full flex items-center justify-between px-3 py-2 mb-2 text-[10px] font-semibold text-gray-400 uppercase tracking-widest hover:text-gray-600 transition-colors"
                  >
                    <span className="flex items-center gap-2">
                      <Settings2 className="w-3.5 h-3.5" />
                      Administration
                    </span>
                    {expandedSections.admin ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                  </button>
                )}
                {sidebarCollapsed && (
                  <div className="h-px bg-gray-200 my-3 mx-2" />
                )}
                {(sidebarCollapsed || expandedSections.admin) && (
                  <div className="space-y-1.5">
                    {adminItems.map((item) => (
                      <NavItem key={item.to} {...item} collapsed={sidebarCollapsed} />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Super Admin Section */}
            {isSuperAdmin && (
              <div className="mb-4">
                {!sidebarCollapsed && (
                  <div className="px-3 py-2 mb-2 text-[10px] font-semibold text-amber-500 uppercase tracking-widest flex items-center gap-2">
                    <Crown className="w-3.5 h-3.5" /> Super Admin
                  </div>
                )}
                <div className="space-y-1.5">
                  <NavItem to="/super-admin" label="Restaurants" icon={Crown} collapsed={sidebarCollapsed} />
                </div>
              </div>
            )}
          </nav>

          {/* User Info & Logout */}
          <div className={`border-t border-gray-100 flex-shrink-0 ${showTrialBanner ? (sidebarCollapsed ? 'p-1' : 'p-2') : (sidebarCollapsed ? 'p-2' : 'p-4')}`}>
            {sidebarCollapsed ? (
              <div className="flex flex-col items-center gap-2">
                <button 
                  onClick={openSwitchDialog}
                  className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#D8F3DC] to-[#B7E4C7] flex items-center justify-center cursor-pointer hover:shadow-md transition-shadow"
                  title={`${user?.first_name} ${user?.last_name} - Cliquer pour changer`}
                  data-testid="user-switch-btn"
                >
                  <span className="text-[#1B4332] font-semibold text-sm">
                    {user?.first_name?.[0]}{user?.last_name?.[0]}
                  </span>
                </button>
                <Button 
                  variant="ghost"
                  size="icon"
                  className={`w-10 rounded-xl hover:bg-red-50 hover:text-red-600 ${showTrialBanner ? 'h-8' : 'h-10'}`}
                  onClick={handleLogout}
                  data-testid="logout-btn"
                  title="Déconnexion"
                >
                  <LogOut className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <>
                <button 
                  onClick={openSwitchDialog}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 bg-gradient-to-r from-gray-50 to-gray-100/50 rounded-xl ${showTrialBanner ? 'mb-1.5' : 'mb-3'} hover:from-[#D8F3DC]/30 hover:to-[#D8F3DC]/10 transition-colors cursor-pointer`}
                  data-testid="user-switch-btn"
                >
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#D8F3DC] to-[#B7E4C7] flex items-center justify-center shadow-sm">
                    <span className="text-[#1B4332] font-semibold text-sm">
                      {user?.first_name?.[0]}{user?.last_name?.[0]}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0 text-left">
                    <p className="font-medium text-gray-900 truncate text-sm">
                      {user?.first_name} {user?.last_name}
                    </p>
                    <p className="text-xs text-gray-400 truncate">Changer de session</p>
                  </div>
                </button>
                <Button 
                  variant="outline" 
                  className={`w-full text-gray-500 hover:text-red-600 hover:border-red-200 hover:bg-red-50 rounded-xl ${showTrialBanner ? 'h-9 text-sm' : 'h-10'}`}
                  onClick={handleLogout}
                  data-testid="logout-btn"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Déconnexion
                </Button>
              </>
            )}
          </div>
        </div>
      </aside>

      {/* Trial Banner - Full Width Fixed Top */}
      {showTrialBanner && (
        <div 
          className={`fixed top-0 left-0 right-0 shadow-sm transition-all duration-300 ${
            sidebarWidth === 'w-20' ? 'lg:left-20' : 'lg:left-64'
          } ${
            isTrialExpired 
              ? 'bg-[#C53030] border-b border-red-700 z-40' 
              : 'bg-white border-b border-gray-200 z-40'
          }`}
          data-testid={isTrialExpired ? 'trial-banner-expired' : 'trial-banner-active'}
        >
          <div className="flex items-center justify-between px-6 py-2.5 max-w-7xl mx-auto">
            <div className="flex items-center gap-3">
              {!isTrialExpired ? (
                <>
                  <div className="flex items-center gap-2 bg-[#D8F3DC] px-3 py-1.5 rounded-lg">
                    <Clock className="w-4 h-4 text-[#1B4332]" />
                    <span className="font-bold text-[#1B4332] text-sm">
                      {trialDaysLeft}
                    </span>
                  </div>
                  <span className="text-gray-700 font-medium text-sm">
                    {trialDaysLeft > 0 
                      ? `jour${trialDaysLeft > 1 ? 's' : ''} d'essai gratuit restant${trialDaysLeft > 1 ? 's' : ''}`
                      : 'Essai gratuit terminé'
                    }
                  </span>
                  <span className="hidden sm:inline text-gray-500 text-sm">
                    • Toutes les fonctionnalités incluses
                  </span>
                </>
              ) : (
                <span className="text-white font-semibold text-sm">
                  Période d'essai terminée. Abonnez-vous pour continuer de profiter des fonctionnalités
                </span>
              )}
            </div>
            <Button
              onClick={() => navigate('/admin/settings?tab=subscription')}
              size="sm"
              className={`font-semibold h-8 px-4 text-sm ${
                isTrialExpired
                  ? 'bg-white hover:bg-gray-100 text-[#C53030]'
                  : 'bg-[#1B4332] hover:bg-[#0D2E1F] text-white'
              }`}
              data-testid="trial-banner-cta"
            >
              S'abonner maintenant
            </Button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className={`${showTrialBanner ? 'pt-[calc(3.5rem+2.75rem+env(safe-area-inset-top))] lg:pt-[2.75rem]' : 'pt-[calc(3.5rem+env(safe-area-inset-top))] lg:pt-0'} ${mainMargin} transition-all duration-300`}>
        <div className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto pb-24 lg:pb-8">
          <Outlet />
        </div>
      </main>

      {/* Mobile Bottom Navigation */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-lg border-t border-gray-200">
        <div className="safe-area-bottom">
          <div className="flex items-center justify-around h-16 max-w-lg mx-auto px-2">
            {isAdmin && (
              <NavLink
                to="/dashboard"
                className={({ isActive }) =>
                  `flex flex-col items-center gap-0.5 p-2 rounded-xl min-w-[60px] transition-all relative ${
                    isActive ? 'text-[#1B4332] bg-[#D8F3DC]' : 'text-gray-400'
                  }`
                }
              >
                <BarChart3 className="w-5 h-5" />
                <span className="text-[10px] font-medium">Stats</span>
                {!hasVisitedPage('/dashboard') && (
                  <span className="absolute top-1 right-1 w-4 h-4 bg-[#1B4332] text-white text-[9px] rounded-full flex items-center justify-center font-semibold">
                    1
                  </span>
                )}
              </NavLink>
            )}
            <NavLink
              to="/catalog"
              className={({ isActive }) =>
                `flex flex-col items-center gap-0.5 p-2 rounded-xl min-w-[60px] transition-all relative ${
                  isActive ? 'text-[#1B4332] bg-[#D8F3DC]' : 'text-gray-400'
                }`
              }
            >
              <ShoppingCart className="w-5 h-5" />
              <span className="text-[10px] font-medium">Commander</span>
              {!hasVisitedPage('/catalog') && (
                <span className="absolute top-1 right-1 w-4 h-4 bg-[#1B4332] text-white text-[9px] rounded-full flex items-center justify-center font-semibold">
                  1
                </span>
              )}
            </NavLink>
            <NavLink
              to="/orders"
              className={({ isActive }) =>
                `flex flex-col items-center gap-0.5 p-2 rounded-xl min-w-[60px] transition-all relative ${
                  isActive ? 'text-[#1B4332] bg-[#D8F3DC]' : 'text-gray-400'
                }`
              }
            >
              <ClipboardList className="w-5 h-5" />
              <span className="text-[10px] font-medium">Commandes</span>
              {!hasVisitedPage('/orders') && (
                <span className="absolute top-1 right-1 w-4 h-4 bg-[#1B4332] text-white text-[9px] rounded-full flex items-center justify-center font-semibold">
                  1
                </span>
              )}
            </NavLink>
            <NavLink
              to="/deliveries"
              className={({ isActive }) =>
                `flex flex-col items-center gap-0.5 p-2 rounded-xl min-w-[60px] transition-all relative ${
                  isActive ? 'text-[#1B4332] bg-[#D8F3DC]' : 'text-gray-400'
                }`
              }
            >
              <PackageCheck className="w-5 h-5" />
              <span className="text-[10px] font-medium">Livraisons</span>
              {!hasVisitedPage('/deliveries') && (
                <span className="absolute top-1 right-1 w-4 h-4 bg-[#1B4332] text-white text-[9px] rounded-full flex items-center justify-center font-semibold">
                  1
                </span>
              )}
            </NavLink>
            {cartCount > 0 && (
              <NavLink
                to="/cart"
                className={({ isActive }) =>
                  `flex flex-col items-center gap-0.5 p-2 rounded-xl min-w-[60px] relative transition-all ${
                    isActive ? 'text-[#1B4332] bg-[#D8F3DC]' : 'text-gray-400'
                  }`
                }
              >
                <div className="relative">
                  <ShoppingCart className="w-5 h-5" />
                  <span className="absolute -top-2 -right-2 w-4 h-4 bg-[#1B4332] text-white text-[10px] rounded-full flex items-center justify-center font-medium">
                    {cartCount}
                  </span>
                </div>
                <span className="text-[10px] font-medium">Panier</span>
              </NavLink>
            )}
          </div>
        </div>
      </nav>

      {/* Quick Switch User Dialog */}
      <Dialog open={switchDialogOpen} onOpenChange={setSwitchDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCircle className="w-5 h-5 text-[#1B4332]" />
              Changer de session
            </DialogTitle>
          </DialogHeader>
          
          {loadingUsers ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-[#1B4332]" />
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-gray-500">
                Sélectionnez un utilisateur et entrez son mot de passe
              </p>
              
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {users.map((u) => (
                  <div 
                    key={u.id}
                    className={`p-3 border rounded-xl transition-colors ${
                      switchingUser === u.id ? 'border-[#1B4332] bg-[#D8F3DC]/20' : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#D8F3DC] to-[#B7E4C7] flex items-center justify-center">
                        <span className="text-[#1B4332] font-semibold text-sm">
                          {u.first_name?.[0]}{u.last_name?.[0]}
                        </span>
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{u.first_name} {u.last_name}</p>
                        <p className="text-xs text-gray-500">
                          {u.is_admin ? 'Admin' : u.is_responsable ? 'Responsable' : 'Utilisateur'}
                          {u.team_name && ` • ${u.team_name}`}
                        </p>
                      </div>
                    </div>
                    
                    <div className="mt-3 flex gap-2">
                      <Input
                        type="password"
                        placeholder="Mot de passe"
                        className="flex-1"
                        value={switchingUser === u.id ? passwordInput : ''}
                        onChange={(e) => {
                          setSwitchingUser(u.id);
                          setPasswordInput(e.target.value);
                        }}
                        onFocus={() => setSwitchingUser(u.id)}
                        onKeyDown={(e) => e.key === 'Enter' && handleQuickSwitch(u)}
                        disabled={switchLoading}
                      />
                      <Button
                        onClick={() => handleQuickSwitch(u)}
                        disabled={switchingUser !== u.id || !passwordInput || switchLoading}
                        className="bg-[#1B4332] hover:bg-[#0D2E1F]"
                      >
                        {switchLoading && switchingUser === u.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          'Connexion'
                        )}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Layout;
